import * as React from "react"
import {
  Box,
  Button,
  ButtonProps,
  Icon,
  Image,
  LayoutProps,
} from "@chakra-ui/react"
import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons"
import { FaExpand, FaTimes } from "react-icons/fa"
import { GatsbyImage } from "gatsby-plugin-image"
import ImageGallery from "react-image-gallery"
import CSS from "csstype"
import "react-image-gallery/styles/css/image-gallery.css"

import { IQueriedContentfulSVG, IQueriedGatsbyImage } from "./utils"
import { MediaContext } from "../components/CoreUI/MediaContext"
import type CoreUI from "../components/CoreUI/types"

type ValidGalleryImage = IQueriedContentfulSVG | IQueriedGatsbyImage

interface ICarouselImage {
  image: ValidGalleryImage
  original: string
  thumbnail: string
}

interface UniversalImageGalleryProps {
  buttonProps?: ButtonProps
  galleryProps?: { [key: string]: any }
  fullscreenProps?: { [key: string]: any }
  fullscreenHeight?: `100%` | LayoutProps[`h`]
  itemWrapperProps?: { [key: string]: any }
  onBeforeSlide?: (index: number) => void
  onAfterSlide?: (index: number) => void
  showNavShadow?: boolean
  thumbnailProps?: object
}

interface HOCImageGalleryProps extends UniversalImageGalleryProps {
  images: IQueriedContentfulSVG[] | IQueriedGatsbyImage[]
}

interface BasicImageGalleryProps extends UniversalImageGalleryProps {
  images: ICarouselImage[]
  onToggleFullscreen: (value: boolean) => void
  renderer: (image: ValidGalleryImage) => JSX.Element
}

interface GatsbyImageGalleryProps extends HOCImageGalleryProps {
  images: IQueriedGatsbyImage[]
  objectFit?: CSS.Property.ObjectFit
}

interface SVGImageGalleryProps extends HOCImageGalleryProps {
  images: IQueriedContentfulSVG[]
}

function convertGatsbyImagesToObjectArray(
  images: IQueriedGatsbyImage[]
): ICarouselImage[] {
  const imgObjArr: ICarouselImage[] = images.map(
    (img: IQueriedGatsbyImage) => ({
      image: img,
      original: img.gatsbyImageData.images.fallback.src,
      thumbnail: img.gatsbyImageData.images.fallback.src,
    })
  )

  return imgObjArr
}

function convertSvgFilesToObjectArray(
  images: IQueriedContentfulSVG[]
): ICarouselImage[] {
  const imgObjArr: ICarouselImage[] = images.map(
    (img: IQueriedContentfulSVG) => ({
      image: {
        description: img.description,
        file: {
          url: img.file.url,
        },
      },
      original: img.file.url,
      thumbnail: img.file.url,
    })
  )

  return imgObjArr
}

function BasicImageGallery({
  buttonProps,
  galleryProps = {},
  images,
  onBeforeSlide,
  onAfterSlide,
  onToggleFullscreen,
  renderer,
  showNavShadow = true,
  thumbnailProps,
}: BasicImageGalleryProps): JSX.Element {
  const [isFullscreen, setIsFullscreen] = React.useState<boolean>(false)
  const galleryRef = React.useRef<any>()
  const { reduceMotion } = React.useContext<CoreUI.TMediaContext>(MediaContext)

  if (reduceMotion) {
    galleryProps[`slideDuration`] = 0
  }

  React.useEffect(() => {
    if (reduceMotion && !galleryProps.autoPlay) {
      galleryRef.current.pause()
    }
  }, [reduceMotion])

  React.useEffect(() => {
    onToggleFullscreen(isFullscreen)
  }, [isFullscreen])

  return (
    <ImageGallery
      items={images}
      onBeforeSlide={onBeforeSlide}
      onSlide={onAfterSlide}
      onScreenChange={setIsFullscreen}
      ref={galleryRef}
      renderItem={(item: ICarouselImage) => renderer(item.image)}
      style={{ zIndex: isFullscreen ? 1000 : 1 }}
      renderFullscreenButton={onClick => (
        <Button
          bottom={isFullscreen ? null : `6px`}
          top={isFullscreen ? `6px` : null}
          color="whiteAlpha.900"
          onClick={onClick}
          p={0}
          pos="absolute"
          right="6px"
          variant="unstyled"
          zIndex={2}
          _hover={{
            color: `white`,
            transform: `scale(1.1)`,
          }}
          {...buttonProps}
        >
          <Icon
            as={isFullscreen ? FaTimes : FaExpand}
            filter={showNavShadow ? `drop-shadow(0 1px 2px #333)` : null}
            h={6}
            w={6}
          />
        </Button>
      )}
      renderLeftNav={onClick => (
        <Button
          color="whiteAlpha.900"
          left="6px"
          onClick={onClick}
          p={0}
          pos="absolute"
          top="calc(50% - var(--chakra-sizes-4))"
          variant="unstyled"
          zIndex={2}
          _hover={{
            color: `white`,
            transform: `scale(1.1)`,
          }}
          {...buttonProps}
        >
          <ArrowBackIcon
            filter={showNavShadow ? `drop-shadow(0 1px 2px #333)` : null}
            h={8}
            w={8}
          />
        </Button>
      )}
      renderRightNav={onClick => (
        <Button
          color="whiteAlpha.900"
          onClick={onClick}
          p={0}
          pos="absolute"
          right="6px"
          top="calc(50% - var(--chakra-sizes-4))"
          transition="0.3s"
          variant="unstyled"
          zIndex={2}
          _hover={{
            color: `white`,
            transform: `scale(1.1)`,
          }}
          {...buttonProps}
        >
          <ArrowForwardIcon
            filter={showNavShadow ? `drop-shadow(0 1px 2px #333)` : null}
            h={8}
            w={8}
          />
        </Button>
      )}
      renderThumbInner={({ image: { description, file, gatsbyImageData } }) =>
        file ? (
          <Image
            alt={description}
            h="100%"
            rounded="sm"
            shadow="sm"
            src={file.url}
            w="100%"
            {...thumbnailProps}
          />
        ) : (
          <Image
            alt={description}
            as={GatsbyImage}
            className="squared"
            h={[`50px`, `75px`, , , `100px`]}
            image={gatsbyImageData}
            shadow="sm"
            style={{ objectFit: `cover` }}
            w="100%"
            {...thumbnailProps}
          />
        )
      }
      showPlayButton={false}
      useBrowserFullscreen={false}
      {...galleryProps}
    />
  )
}

export default BasicImageGallery

function SVGImageGallery(props: SVGImageGalleryProps): JSX.Element {
  const [fullscreen, setFullscreen] = React.useState<boolean>(false)

  const { fullscreenProps = {}, images, itemWrapperProps = {} } = props
  const _images: ICarouselImage[] = convertSvgFilesToObjectArray(images)

  return (
    <BasicImageGallery
      {...props}
      images={_images}
      onToggleFullscreen={setFullscreen}
      renderer={(image: IQueriedContentfulSVG) => (
        <Box h="100%" w="100%" {...itemWrapperProps}>
          <Image
            alt={image.description}
            h="500px"
            src={image.file.url}
            w="100%"
            {...(fullscreen ? fullscreenProps : {})}
          />
        </Box>
      )}
    />
  )
}

function GatsbyImageGallery(props: GatsbyImageGalleryProps): JSX.Element {
  const [fullscreen, setFullscreen] = React.useState<boolean>(false)

  const {
    fullscreenProps = {},
    fullscreenHeight = `100%`,
    images,
    itemWrapperProps = {},
    objectFit = `cover`,
  } = props
  const _images: ICarouselImage[] = convertGatsbyImagesToObjectArray(images)

  const fsProps = {
    ...itemWrapperProps,
    h: fullscreen ? fullscreenHeight : `inherit`,
  }

  return (
    <BasicImageGallery
      {...props}
      images={_images}
      onToggleFullscreen={setFullscreen}
      renderer={(image: IQueriedGatsbyImage) => (
        <Box
          d="flex"
          h={[`325px`, `450px`, `550px`, `600px`]}
          justifyContent="center"
          m="auto"
          maxH="80vh"
          py={fullscreen ? 4 : 0}
          w="100%"
          {...(fullscreen ? fsProps : itemWrapperProps)}
        >
          <GatsbyImage
            alt={image.description}
            className="squared"
            image={image.gatsbyImageData}
            objectFit={objectFit}
            {...(fullscreen ? fullscreenProps : {})}
          />
        </Box>
      )}
    />
  )
}

export { GatsbyImageGallery, SVGImageGallery }
